import { getTotal } from "./utils";

export const performanceDashboard = ({ report, brands }) => [
  {
    id: "pd_best_ranking_categories",
    chartBlock: {
      title: "Best Ranking Categories",
      description:
        "This highlights the strongest-performing product categories in search results, with lower numbers indicating better average rankings. Categories appearing at the top of this list demonstrate successful optimization and can serve as models for improving other areas. These well-performing categories show where current strategies are working effectively and should be maintained or expanded.",
      cols: ["Category", "Ranking"],
      fields: ["category", "ranking"],
      sortField: "ranking",
      sortOrder: 1,
      // footer: [
      //   "Grand Total",
      //   getTotal({
      //     report,
      //     sheet: "pd_best_ranking_categories",
      //     field: "ranking",
      //   }),
      // ],
    },
  },
  {
    id: "pd_worst_ranking_categories",
    chartBlock: {
      title: "Worst Ranking Categories",
      description:
        "This identifies product categories where search rankings need the most attention, with higher numbers indicating weaker performance. Categories shown here represent the biggest opportunities to climb search result rankings and capture more organic traffic through targeted optimization efforts. Focusing improvement efforts on these areas will likely yield the most significant gains in search visibility and website traffic.",
      cols: ["Category", "Ranking"],
      fields: ["category", "ranking"],
      sortField: "ranking",
      sortOrder: -1,
      swap: true,
      // footer: [
      //   "Grand Total",
      //   getTotal({
      //     report,
      //     sheet: "pd_worst_ranking_categories",
      //     field: "ranking",
      //   }),
      // ],
    },
  },
  {
    id: "pd_traffic_opps",
    chartBlock: {
      title: "What are the biggest traffic gain opportunities?",
      description:
        "This section identifies the biggest opportunities to increase website traffic by showing which product categories have the highest potential for growth. The numbers represent estimated additional visitors that could be attracted annually through improved search engine optimization. The categories at the top of this list show the largest opportunities, indicating where marketing efforts should be focused to maximize return on investment and drive the most significant traffic increases.",
      cols: ["Category", "Yearly Traffic Gain In #1"],
      fields: ["category", "traffic_gain"],
      sortField: "traffic_gain",
      sortOrder: -1,
      footer: [
        "Grand Total",
        getTotal({ report, sheet: "pd_traffic_opps", field: "traffic_gain" }),
      ],
    },
  },
  {
    id: "pd_revenue_opps",
    chartBlock: {
      title: "What are the biggest revenue gain opportunities?",
      description:
        "This view translates traffic opportunities into estimated revenue potential, showing the financial impact of improving search rankings for each category. The monetary values represent the potential additional revenue that could be generated annually through increased organic traffic. This helps prioritize optimization efforts based on business impact, focusing resources on categories that offer the highest revenue potential rather than just traffic volume.",
      cols: ["Category", "Yearly Revenue Gain In #1"],
      fields: ["category", "revenue_gain"],
      sortField: "revenue_gain",
      sortOrder: -1,
      swap: true,
      footer: [
        "Grand Total",
        getTotal({ report, sheet: "pd_revenue_opps", field: "revenue_gain" }),
      ],
    },
  },
  {
    id: "pd_ranking_opps",
    chartBlock: {
      title: "How are keyword ranking opportunities grouped?",
      description: `This analysis categorizes keyword opportunities based on the effort required to improve rankings. "Work Needed" represents keywords requiring significant optimization effort, while "Quick Win" identifies keywords that could improve with minimal effort. "Maintain" shows keywords already performing well that need ongoing attention. This breakdown helps prioritize SEO activities by balancing potential impact with resource requirements.`,
      desc: (
        <div
          style={{
            lineHeight: "normal",
            marginTop: "30px",
            paddingLeft: "10px",
          }}
        >
          <p>
            <b>Maintain</b> = Rank of 1 to 3
          </p>
          <p>
            <b>Striking Distance</b> = Rank of 4 to 10
          </p>
          <p>
            <b>Nudge</b> = Rank of 11 to 20
          </p>
          <p>
            <b>Quick Win</b> = Rank of 21 to 30
          </p>
          <p>
            <b>Work Needed</b> = Rank of 31+
          </p>
        </div>
      ),
      cols: ["Category", "Opportunity Breakdown"],
      fields: ["category", "opp"],
      sortField: "opp",
      sortOrder: -1,
      // footer: [
      //   "Grand Total",
      //   getTotal({ report, sheet: "pd_ranking_opps", field: "opp" }),
      // ],
    },
  },
  {
    id: "pd_url_ranking",
    tableBlock: {
      title: "Keywords Ranking Per URL (Top 10 Pages)",
      description:
        "This section shows which individual website pages are generating the most keyword rankings and organic visibility. The count indicates how many different search terms each page is ranking for, revealing your strongest content assets. Pages with higher ranking counts demonstrate successful optimization and content strategies that can be replicated across other areas of the website.",
      cols: ["Category", "Count of Ranking URL"],
      fields: ["category", "ranking"],
      sortField: "ranking",
      sortOrder: -1,
      footer: [
        "Grand Total",
        getTotal({ report, sheet: "pd_url_ranking", field: "ranking" }),
      ],
      container: true,
    },
  },
  {
    id: "pd_biggest_search_categories",
    chartBlock: {
      title: "What are the biggest searched for categories?",
      description:
        "This view shows which product categories have the highest search demand based on actual user search behavior. The numbers represent the total annual search volume for each category, indicating market interest and potential traffic available. Categories with higher search volumes represent larger market opportunities and should be prioritized for content creation and optimization efforts.",
      cols: ["Category", "Yearly Traffic Gain In #1"],
      fields: ["category", "volume"],
      sortField: "volume",
      sortOrder: -1,
      footer: [
        "Grand Total",
        getTotal({
          report,
          sheet: "pd_biggest_search_categories",
          field: "volume",
        }),
      ],
    },
  },
  {
    id: "pd_keyword_intent",
    tableBlock: {
      title: "How are the keywords broken down by intent?",
      description:
        "This breakdown categorizes keywords by user intent, showing whether searchers are looking to make purchases (Transactional), gather information (Informational), or research options (Commercial). Understanding intent distribution helps tailor content strategy - transactional keywords drive sales, informational keywords build authority, and commercial keywords capture users in the consideration phase. The traffic potential for each intent type guides content development priorities.",
      cols: [
        "Intent",
        "Number of Keywords",
        "Est. Current Clicks",
        "Expected Yearly Traffic In #1",
      ],
      fields: ["intent", "keywords", "clicks", "traffic"],
      sortField: "traffic",
      sortOrder: -1,
      footer: [
        "Grand Total",
        getTotal({ report, sheet: "pd_keyword_intent", field: "keywords" }),
        getTotal({ report, sheet: "pd_keyword_intent", field: "clicks" }),
        getTotal({ report, sheet: "pd_keyword_intent", field: "traffic" }),
      ],
      container: true,
    },
  },
  {
    id: "pd_page_template",
    tableBlock: {
      title: "How are the keywords broken down by page template?",
      cols: [
        "Page Template",
        "Number of Keywords",
        "Est. Current Clicks",
        "Expected Yearly Traffic In #1",
      ],
      fields: ["page", "keywords", "clicks", "traffic"],
      sortField: "traffic",
      sortOrder: -1,
      footer: [
        "Grand Total",
        getTotal({ report, sheet: "pd_page_template", field: "keywords" }),
        getTotal({ report, sheet: "pd_page_template", field: "clicks" }),
        getTotal({ report, sheet: "pd_page_template", field: "traffic" }),
      ],
      container: true,
    },
  },
];
